import bankUxEn from "./messages/bankUx.en";
import bankUxRu from "./messages/bankUx.ru";
import bankUxLv from "./messages/bankUx.lv";
import commonEn from "./messages/common.en";
import commonFr from "./messages/common.fr";
import commonLv from "./messages/common.lv";
import commonRu from "./messages/common.ru";
import a3PaySetupEn from "./messages/a3PaySetup.en";
import a3PaySetupLv from "./messages/a3PaySetup.lv";
import a3PaySetupRu from "./messages/a3PaySetup.ru";
import fiscalGatewaySetupEn from "./messages/fiscalGatewaySetup.en";
import fiscalGatewaySetupLv from "./messages/fiscalGatewaySetup.lv";
import fiscalGatewaySetupRu from "./messages/fiscalGatewaySetup.ru";
import guestEn from "./messages/guest.en";
import guestFr from "./messages/guest.fr";
import guestLv from "./messages/guest.lv";
import guestRu from "./messages/guest.ru";
import tableServiceSetupEn from "./messages/tableServiceSetup.en";
import tableServiceSetupLv from "./messages/tableServiceSetup.lv";
import tableServiceSetupRu from "./messages/tableServiceSetup.ru";
import type { Locale } from "./types";

const STORAGE_KEY = "guest_locale";
export const SUPPORTED_GUEST_LOCALES: Locale[] = ["en", "lv", "ru", "fr"];
export const GUEST_LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  lv: "Latviski",
  ru: "Русский",
  fr: "Français",
};
const messagesByLocale: Record<Locale, Record<string, string>> = {
  lv: { ...commonLv, ...bankUxLv, ...guestLv, ...tableServiceSetupLv, ...a3PaySetupLv, ...fiscalGatewaySetupLv },
  en: { ...commonEn, ...bankUxEn, ...guestEn, ...tableServiceSetupEn, ...a3PaySetupEn, ...fiscalGatewaySetupEn },
  ru: { ...commonRu, ...bankUxRu, ...guestRu, ...tableServiceSetupRu, ...a3PaySetupRu, ...fiscalGatewaySetupRu },
  fr: { ...commonFr, ...guestFr },
};

const normalizeLocale = (value: string | null | undefined): Locale | null => {
  if (!value) return null;
  const lower = value.toLowerCase();
  if (SUPPORTED_GUEST_LOCALES.includes(lower as Locale)) {
    return lower as Locale;
  }
  if (lower.startsWith("en")) return "en";
  if (lower.startsWith("lv")) return "lv";
  if (lower.startsWith("ru")) return "ru";
  if (lower.startsWith("fr")) return "fr";
  return null;
};

export const resolveLocale = (preferred?: string | null): Locale => {
  const byParam = normalizeLocale(preferred);
  if (byParam) return byParam;

  try {
    const stored = normalizeLocale(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    // ignore storage errors
  }

  if (typeof navigator !== "undefined") {
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const lang of langs) {
      const normalized = normalizeLocale(lang);
      if (normalized) return normalized;
    }
  }

  return "en";
};

let activeLocale: Locale = "en";

export const initGuestLocale = (preferred?: string | null) => {
  activeLocale = resolveLocale(preferred);
  return activeLocale;
};

export const setGuestLocale = (locale: Locale) => {
  activeLocale = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore storage errors
  }
  return activeLocale;
};

export const getActiveLocale = () => activeLocale;
export const getGuestLocaleStorageKey = () => STORAGE_KEY;

const formatWithParams = (template: string, params?: Record<string, string | number>) => {
  if (!params) return template;
  return Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`{${key}}`, "g"), String(value)),
    template,
  );
};

export const t = (key: string, params?: Record<string, string | number>, localeArg?: Locale) => {
  const locale = localeArg ?? activeLocale;
  const dictionary = messagesByLocale[locale] ?? messagesByLocale.en;
  const fallback = messagesByLocale.en;
  const template = dictionary[key] ?? fallback[key] ?? key;
  return formatWithParams(template, params);
};
